import { useState, useCallback, useRef, useEffect } from "react";

interface Column {
  key: string;
  label: string;
  width: string;
  type?: string;
}

interface UseResizableColumnsOptions {
  columns: Column[];
  minWidth?: number;
  storageKey?: string;
}

export function useResizableColumns({ columns, minWidth = 50, storageKey }: UseResizableColumnsOptions) {
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>(() => {
    // Try to load from localStorage
    if (storageKey) {
      try {
        const saved = localStorage.getItem(storageKey);
        if (saved) {
          return JSON.parse(saved);
        }
      } catch (e) {
        // Ignore parse errors
      }
    }
    
    // Initialize from column definitions
    const widths: Record<string, number> = {};
    columns.forEach(col => {
      widths[col.key] = parseInt(col.width) || 150;
    });
    return widths;
  });

  const resizingRef = useRef<{
    columnKey: string;
    startX: number;
    startWidth: number;
  } | null>(null);

  // Save to localStorage when widths change
  useEffect(() => {
    if (storageKey) {
      localStorage.setItem(storageKey, JSON.stringify(columnWidths));
    }
  }, [columnWidths, storageKey]);

  const handleResizeStart = useCallback((e: React.MouseEvent, columnKey: string) => {
    e.preventDefault();
    e.stopPropagation();
    
    resizingRef.current = {
      columnKey,
      startX: e.clientX,
      startWidth: columnWidths[columnKey] || 150,
    };

    const handleMouseMove = (moveEvent: MouseEvent) => {
      if (!resizingRef.current) return;
      
      const diff = moveEvent.clientX - resizingRef.current.startX;
      const newWidth = Math.max(minWidth, resizingRef.current.startWidth + diff);
      
      setColumnWidths(prev => ({
        ...prev,
        [resizingRef.current!.columnKey]: newWidth,
      }));
    };

    const handleMouseUp = () => {
      resizingRef.current = null;
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  }, [columnWidths, minWidth]);

  const getColumnWidth = useCallback((columnKey: string): number => {
    return columnWidths[columnKey] || 150;
  }, [columnWidths]);

  const resetWidths = useCallback(() => {
    const widths: Record<string, number> = {};
    columns.forEach(col => {
      widths[col.key] = parseInt(col.width) || 150;
    });
    setColumnWidths(widths);
  }, [columns]);

  return {
    columnWidths,
    handleResizeStart,
    getColumnWidth,
    resetWidths,
  };
}
